import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, gameApi } from "@/lib/api";
import { HorseProfileHeader } from "@/components/game/GameViews";
import { StatBar } from "@/components/game/StatBar";
import { toast } from "sonner";
import { Loader2, ChevronLeft, Sparkles, Heart, Gift, X } from "lucide-react";

const NAT_KEYS = ["speed", "acceleration", "agility", "strength", "stamina", "balance", "athleticism", "responsiveness", "bravery", "cow_sense", "movement", "scope"];
const titleCase = (k) => k.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

const Section = ({ title, children }) => (
  <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-4" data-testid="public-section">
    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-secondary">{title}</p>
    {children}
  </div>
);

const GiftModal = ({ gift, onClose, onGave }) => {
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const { pn, aid } = useParams();
  const giverMaxed = gift.limits.giver_used >= gift.limits.giver_max;
  const horseMaxed = gift.limits.horse_used >= gift.limits.horse_max;
  const give = () => {
    if (!sel || busy) return;
    setBusy(true);
    gameApi.post("/game/friends/gift", { player_number: Number(pn), horse_id: aid, item_id: sel })
      .then((r) => { toast.success(`You gave ${gift.horse_name} some ${r.data.item.name}! 💚`); onGave(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't send that treat."))
      .finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-[120] grid place-items-end bg-black/50 sm:place-items-center" data-testid="gift-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-3xl bg-card p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center gap-2">
          <Gift size={18} className="text-secondary" />
          <h3 className="font-display text-lg font-bold text-primary">Treat {gift.horse_name}</h3>
          <button data-testid="gift-modal-close" onClick={onClose} className="ml-auto rounded-full p-1 text-muted-foreground"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          {gift.horse_name} has had {gift.limits.horse_used}/{gift.limits.horse_max} friend treats today. You may give one treat per horse per day.
        </p>
        {horseMaxed ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="gift-horse-maxed">
            {gift.horse_name} has had all the friend treats it can today. Come back tomorrow!
          </p>
        ) : giverMaxed ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="gift-giver-maxed">
            You've already treated {gift.horse_name} today. Come back tomorrow!
          </p>
        ) : gift.items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="gift-no-items">
            You have no treats or feed to give. Buy some in the Feed Room!
          </p>
        ) : (
          <>
            <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="gift-item-list">
              {gift.items.map((it) => (
                <button key={it.id} data-testid={`gift-item-${it.id}`} onClick={() => setSel(it.id)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left active:scale-[0.99] ${sel === it.id ? "border-primary bg-primary/5" : "border-border bg-background"}`}>
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary/15 text-secondary">
                    {it.category === "treats" ? <Heart size={16} /> : <Sparkles size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-primary">{it.name}</p>
                    <p className="text-[11px] capitalize text-muted-foreground">{it.category} · {it.qty} in your bag</p>
                  </div>
                  {sel === it.id && <span className="text-xs font-bold text-primary">Selected</span>}
                </button>
              ))}
            </div>
            <button data-testid="gift-confirm-btn" disabled={!sel || busy} onClick={give}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-40 active:scale-95">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><Gift size={16} /> Give Treat</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function GameHorsePublic() {
  const { pn, aid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [gift, setGift] = useState(null);
  const [hearts, setHearts] = useState(0);
  const [showGift, setShowGift] = useState(false);

  useEffect(() => {
    axios.get(`${API}/game/share/horse/${pn}/${aid}`)
      .then((r) => { setData(r.data); setHearts(r.data.horse?.gift_hearts || 0); })
      .catch((e) => setErr(e.response?.status === 404 ? "This horse profile is not available." : "Could not load this horse."));
    if (localStorage.getItem("bmm_game_token")) {
      gameApi.get(`/game/friends/gift/options?player_number=${pn}&horse_id=${aid}`)
        .then((r) => { if (r.data.eligible) setGift(r.data); })
        .catch(() => {});
    }
  }, [pn, aid]);

  const refreshGift = () => gameApi.get(`/game/friends/gift/options?player_number=${pn}&horse_id=${aid}`)
    .then((r) => { if (r.data.eligible) setGift(r.data); }).catch(() => {});

  if (err) return (
    <div className="min-h-screen bg-background pb-10" data-testid="public-horse-error">
      <div className="px-4 pt-6"><Link to="/game" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-primary"><ChevronLeft size={15} /> Go to the Game</Link></div>
      <div className="mx-4 mt-8 rounded-2xl border border-dashed border-border bg-card p-8 text-center">
        <p className="font-display text-lg font-bold text-primary">Not available</p>
        <p className="mt-1 text-sm text-muted-foreground">{err}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="grid min-h-screen place-items-center bg-background text-muted-foreground" data-testid="public-horse-loading">
      <p className="flex items-center gap-2 text-sm"><Loader2 className="animate-spin" size={16} /> Loading horse…</p>
    </div>
  );

  const { horse: h, owner, career } = data;
  const nat = h.natural_stats || {};
  const finalStats = h.final_stats || {};
  const finalKeys = Object.keys(finalStats).filter((k) => typeof finalStats[k] === "number");

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="public-horse-view">
      <div className="flex items-center gap-2 px-4 pt-5">
        <span className="text-xs font-bold uppercase tracking-wider text-secondary">120 Days to Makeover</span>
        <Link to="/game" data-testid="public-horse-cta" className="ml-auto rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95">Play the Game</Link>
      </div>

      <HorseProfileHeader horse={h}
        badge={h.discipline ? <p className="mt-1.5 inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700" data-testid="public-career-badge">Career: {h.discipline}</p> : null} />

      <p className="mx-4 mt-2 text-sm text-muted-foreground" data-testid="public-owner">
        Owned by <span className="font-semibold text-primary">{owner.display_name}</span>{owner.stable_name ? ` · ${owner.stable_name}` : ""}
      </p>

      {(hearts > 0 || gift) && (
        <div className="mx-4 mt-3 flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid="gift-hearts-card">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-rose-100"><Heart size={18} className="text-rose-500" fill="currentColor" /></div>
          <div className="flex-1">
            <p className="text-sm font-bold text-primary" data-testid="gift-hearts-count">{hearts} treat{hearts === 1 ? "" : "s"} from friends</p>
            <p className="text-[11px] text-muted-foreground">{owner.display_name}'s friends can spoil {h.name} with treats & feed.</p>
          </div>
          {gift && (
            <button data-testid="give-treat-btn" onClick={() => setShowGift(true)}
              className="flex items-center gap-1.5 rounded-full bg-secondary px-3.5 py-2 text-xs font-bold text-white active:scale-95">
              <Gift size={14} /> Give Treat
            </button>
          )}
        </div>
      )}

      {(h.personality?.length || h.discovered_traits?.length) > 0 && (
        <Section title="Temperament & Traits">
          <div className="flex flex-wrap gap-1.5">
            {(h.personality || []).map((t) => <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">{t}</span>)}
            {(h.discovered_traits || []).map((t) => <span key={t} className="rounded-full bg-secondary/15 px-2 py-0.5 text-[11px] font-semibold text-secondary">{t}</span>)}
          </div>
        </Section>
      )}

      {career && (
        <Section title="Competition Career">
          <p className="text-sm text-muted-foreground">Level <span className="font-bold text-primary">{career.level}</span> · Starts {career.starts} · Wins {career.wins} · Top 3 {career.top3} · {career.earnings}🪙 earned</p>
        </Section>
      )}

      {Object.keys(nat).length > 0 && (
        <Section title="Natural Ability">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {NAT_KEYS.filter((k) => nat[k] != null).map((k) => <StatBar key={k} label={titleCase(k)} value={nat[k]} tone="primary" />)}
          </div>
        </Section>
      )}

      {finalKeys.length > 0 && (
        <Section title="Training Stats">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {finalKeys.map((k) => <StatBar key={k} label={titleCase(k)} value={finalStats[k]} tone={k === "popularity" ? "secondary" : "primary"} />)}
          </div>
        </Section>
      )}

      {(h.learned_skills || []).length > 0 && (
        <Section title="Skills & Training">
          <div className="flex flex-wrap gap-1.5" data-testid="public-skills">
            {h.learned_skills.map((s) => <span key={s} className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">{s}</span>)}
          </div>
        </Section>
      )}

      <div className="mx-4 mt-6 rounded-2xl border border-dashed border-border bg-card/60 p-5 text-center">
        <Sparkles size={22} className="mx-auto text-secondary/60" />
        <p className="mt-2 text-sm text-muted-foreground">Train your own wild mustang over 120 in-game days.</p>
        <Link to="/game" className="mt-3 inline-block rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground active:scale-95">Start Playing</Link>
      </div>

      {showGift && gift && (
        <GiftModal gift={gift} onClose={() => setShowGift(false)}
          onGave={(res) => { setHearts(res.hearts); setShowGift(false); refreshGift(); }} />
      )}
    </div>
  );
}
